import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface Section {
    id: string;
    title: string;
    element: HTMLElement;
}

interface SectionContextValue {
    sections: Section[];
    activeId: string | null;
    registerSection: (section: Section) => void;
    unregisterSection: (id: string) => void;
    scrollToSection: (id: string) => void;
}

const SectionContext = createContext<SectionContextValue | null>(null);

export function useSections() {
    const context = useContext(SectionContext);
    return context;
}

interface SectionProviderProps {
    children: React.ReactNode;
    scrollContainer?: HTMLElement | null;
}

export function SectionProvider({ children, scrollContainer }: SectionProviderProps) {
    const [sections, setSections] = useState<Section[]>([]);
    const [activeId, setActiveId] = useState<string | null>(null);

    // Track if we are currently scrolling programmatically
    const isScrollingRef = useRef(false);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Store latest sections in a ref for access inside scroll handlers
    const sectionsRef = useRef<Section[]>([]);
    useEffect(() => {
        sectionsRef.current = sections;
    }, [sections]);

    const registerSection = useCallback((section: Section) => {
        setSections((prev) => {
            if (prev.find((s) => s.id === section.id)) {
                return prev;
            }
            const newSections = [...prev, section];
            // Sort by document position
            return newSections.sort((a, b) => {
                return a.element.compareDocumentPosition(b.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
            });
        });
    }, []);

    const unregisterSection = useCallback((id: string) => {
        setSections((prev) => prev.filter((s) => s.id !== id));
    }, []);

    const scrollToSection = useCallback((id: string) => {
        const section = sectionsRef.current.find(s => s.id === id);

        // Immediately set active state (Optimistic UI)
        setActiveId(id);
        window.history.pushState(null, '', `#${id}`);
        isScrollingRef.current = true;

        if (!section || !section.element) {
            const domEl = document.getElementById(id);
            if (domEl) {
                domEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } else {
            // Scroll Logic
            const element = section.element;
            const container = scrollContainer;

            if (container) {
                const containerRect = container.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const relativeTop = elementRect.top - containerRect.top;
                const currentScrollTop = container.scrollTop;

                // Offset for header (24px buffer to match sidebar top-6)
                const targetScrollTop = currentScrollTop + relativeTop - 24;

                container.scrollTo({
                    top: targetScrollTop,
                    behavior: 'smooth'
                });
            } else {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }

        // Reset locking flag after animation
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = setTimeout(() => {
            isScrollingRef.current = false;
        }, 800);
    }, [scrollContainer]);

    // Native Scroll Listener Implementation (The "Simple" Way)
    useEffect(() => {
        const container = scrollContainer;
        if (!container) return;

        const handleScroll = () => {
            // 1. Skip if manually scrolling
            if (isScrollingRef.current) return;

            // Force first section active if at top and no hash
            if (container.scrollTop < 10 && !window.location.hash) {
                const currentSections = sectionsRef.current;
                if (currentSections.length > 0) {
                    const firstId = currentSections[0].id;
                    if (activeId !== firstId) {
                        setActiveId(firstId);
                    }
                    return;
                }
            }

            // 2. Define "Trigger Line"
            // "Active" means the section has entered the view.
            // Requirement: "Switch as soon as next Card appears" -> This was the old requirement causing greedy jumps.
            // New Requirement: Active when the section occupies the reading area (top part).
            // Tuned to 30% (0.3) to prevent early highlighting of bottom elements.
            const containerRect = container.getBoundingClientRect();
            const triggerLine = containerRect.top + (containerRect.height * 0.3);

            const currentSections = sectionsRef.current;
            if (currentSections.length === 0) return;

            let newActiveId: string | null = null;

            // 3. Greedy Selection
            // Find the LAST section whose top is ABOVE the trigger line.
            // This effectively selects the "lowest" section currently visible on screen.
            for (const section of currentSections) {
                const rect = section.element.getBoundingClientRect();

                // If this section's top is above the trigger line, it's a candidate.
                if (rect.top < triggerLine) {
                    newActiveId = section.id;
                } else {
                    // Since sections are sorted, once we find one below the line,
                    // all subsequent ones are also below. We can stop.
                    break;
                }
            }

            if (newActiveId && newActiveId !== activeId) {
                setActiveId(newActiveId);
            }
        };

        // Throttle slightly to avoid excessive calculations, but keep it snappy (frame-aligned is best)
        let frameId: number;
        const onScroll = () => {
            cancelAnimationFrame(frameId);
            frameId = requestAnimationFrame(handleScroll);
        };

        container.addEventListener('scroll', onScroll, { passive: true });

        // Initial check
        handleScroll();

        return () => {
            container.removeEventListener('scroll', onScroll);
            cancelAnimationFrame(frameId);
        };
    }, [scrollContainer, activeId]); // Re-bind if activeId changes? No, state update handles it.

    // Handle initial hash scroll
    const location = useLocation();
    const hasScrolledToHashRef = useRef(false);

    // Reset hash scroll flag on pathname change
    useEffect(() => {
        hasScrolledToHashRef.current = false;
    }, [location.pathname]);

    useEffect(() => {
        if (hasScrolledToHashRef.current) return;

        const hash = location.hash.replace('#', '');
        if (!hash) return;

        const section = sections.find(s => s.id === hash);
        if (section) {
            // Use a small timeout to allow layout to stabilize? No, scrollToSection handles it.
            // But we might want to bypass pushState here? No, consistent behavior is fine.
            scrollToSection(hash);
            hasScrolledToHashRef.current = true;
        }
    }, [sections, location.hash, scrollToSection]);

    const contextValue = React.useMemo(() => ({
        sections,
        activeId,
        registerSection,
        unregisterSection,
        scrollToSection
    }), [sections, activeId, registerSection, unregisterSection, scrollToSection]);

    return (
        <SectionContext.Provider value={contextValue}>
            {children}
        </SectionContext.Provider>
    );
}
